/**
 * A collapsible section, used by the workspace's reference panels.
 *
 * Extracted from StudioOverview when the drawer became a full workspace: the
 * timeline column and the lyrics column both need the same pattern, and having it
 * in one file is what stops the two from drifting into slightly different
 * chevrons and paddings.
 *
 * The panel is unmounted while closed rather than hidden, so its contents leave
 * the tab order with it.
 */
import { useState } from 'react';
import type { ReactNode } from 'react';
import { ChevronDown } from 'lucide-react';
import { c } from '../../styles/theme';

export interface DisclosureProps {
  title: string;
  /** Actions for the section, shown only while it is open. */
  trailing?: ReactNode;
  children: ReactNode;
  defaultOpen?: boolean;
}

export default function Disclosure({
  title,
  trailing,
  children,
  defaultOpen = false,
}: DisclosureProps) {
  const [open, setOpen] = useState(defaultOpen);

  return (
    <div style={{ borderTop: `1px solid ${c.border}` }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        <button
          type="button"
          onClick={() => setOpen((o) => !o)}
          aria-expanded={open}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 6,
            flex: 1,
            // `flex: 1` is `1 1 0%`, and a zero basis only shrinks to zero if the item is
            // allowed to: without this the button is floored at its min-content width and
            // `trailing` — the Lyrics section's copy/download/print row, or Song details'
            // — is pushed off the column's edge at 380px.
            minWidth: 0,
            padding: '12px 0',
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            color: c.text1,
            fontSize: 12.5,
            fontWeight: 500,
            fontFamily: 'Inter',
            textAlign: 'left',
          }}
        >
          <ChevronDown
            size={13}
            color={c.text2}
            style={{
              transform: open ? 'rotate(0deg)' : 'rotate(-90deg)',
              transition: 'transform 140ms ease',
              flexShrink: 0,
            }}
          />
          {title}
        </button>
        {/* Only meaningful once the section is open, and hiding it while closed
            keeps each collapsed row to one line of text. */}
        {open && trailing}
      </div>
      {open && <div style={{ paddingBottom: 14 }}>{children}</div>}
    </div>
  );
}
